import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _extends from "@babel/runtime/helpers/extends";
import _regeneratorRuntime from "@babel/runtime/regenerator";

/* eslint-disable no-console */
import React, { Component, Fragment, useState } from "react";
import { useDispatch } from 'react-redux';
import Rex, { updateConfig, useInputDebounce } from "@icg360/rex";
import { Button } from "@icg360/ui-toolkit";
import { formatDate } from "@icg360/ui-toolkit/react-day-picker/moment";
import { array, arrayOf, bool, func, object, shape, string } from "prop-types";
import moment from "moment";
import store from "../../store";
import rexCustomRenderer from "./rexCustomRenderer";
import { addressValidation, addressServicingValidation, analysisValidation } from "../../shared/dispatchers";
import { DATE_FORMAT_PRODUCT, DATE_FORMAT_ISO8601, DATE_FORMAT_UI } from "../../constants";
import { pacDataPropTypes } from "../../shared/propTypes";
import _GroupWrapper from "../../shared/components/GroupWrapper";
import _SubgroupWrapper from "../../shared/components/SubgroupWrapper";
import InputDate from "../../shared/components/InputDate";
import fusions from "../../shared/fusions";
import { getAddressFusionDetails } from "../../shared/utils";

var Select = function Select(_ref) {
  var details = _ref.details,
      id = _ref.id,
      isReadOnly = _ref.isReadOnly,
      isRequired = _ref.isRequired,
      onBlur = _ref.onBlur,
      _onChange = _ref.onChange,
      value = _ref.value;
  return /*#__PURE__*/React.createElement("select", {
    className: "form-control",
    disabled: isReadOnly,
    id: id,
    onChange: function onChange(e) {
      return _onChange(e, null, {
        flush: true
      });
    },
    onBlur: onBlur,
    required: isRequired,
    value: value
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }, "Select one..."), details.map(function (_ref2, i) {
    var detailValue = _ref2.value,
        detailLabel = _ref2.label;
    return /*#__PURE__*/React.createElement("option", {
      value: detailValue,
      key: i
    }, detailLabel);
  }));
};

Select.propTypes = {
  details: arrayOf(shape({
    // These aren't required, as sometimes they'll be empty to provide
    // for an empty first selection in the dropdown
    label: string,
    value: string
  })).isRequired,
  id: string.isRequired,
  isReadOnly: bool.isRequired,
  isRequired: bool.isRequired,
  onBlur: func.isRequired,
  onChange: func.isRequired,
  value: string.isRequired
};

var Input = function Input(_ref3) {
  var id = _ref3.id,
      isReadOnly = _ref3.isReadOnly,
      _onBlur = _ref3.onBlur,
      onChange = _ref3.onChange,
      value = _ref3.value;

  var _useInputDebounce = useInputDebounce({
    value: value,
    onChange: onChange,
    wait: 1000
  }),
      val = _useInputDebounce[0],
      setVal = _useInputDebounce[1],
      flush = _useInputDebounce[2];

  return /*#__PURE__*/React.createElement("input", {
    className: "form-control",
    disabled: isReadOnly,
    id: id,
    onBlur: function onBlur(e) {
      _onBlur(e);

      flush();
    },
    onChange: setVal,
    type: "text",
    value: val
  });
};

Input.propTypes = {
  id: string.isRequired,
  isReadOnly: bool.isRequired,
  onBlur: func.isRequired,
  onChange: func.isRequired,
  value: string.isRequired
};

var Currency = function Currency(_ref4) {
  var value = _ref4.value,
      onChange = _ref4.onChange,
      isReadOnly = _ref4.isReadOnly,
      id = _ref4.id,
      _onBlur2 = _ref4.onBlur;

  var _useInputDebounce2 = useInputDebounce({
    value: value,
    onChange: onChange,
    wait: 1000
  }),
      val = _useInputDebounce2[0],
      setVal = _useInputDebounce2[1],
      flush = _useInputDebounce2[2];

  return /*#__PURE__*/React.createElement("div", {
    className: "input-group"
  }, /*#__PURE__*/React.createElement("span", {
    className: "input-group-addon"
  }, "$"), /*#__PURE__*/React.createElement("input", {
    className: "form-control",
    disabled: isReadOnly,
    id: id,
    onBlur: function onBlur(e) {
      _onBlur2(e);

      flush();
    },
    onChange: function onChange(e) {
      var _e$target, _e$target$value;

      var normalizedVal = (_e$target = e.target) == null ? void 0 : (_e$target$value = _e$target.value) == null ? void 0 : _e$target$value.replace(/\D/g, "");
      setVal(normalizedVal);
    },
    type: "text",
    value: val ? Number(val).toLocaleString() : val
  }));
};

Currency.propTypes = {
  onChange: func.isRequired,
  value: string.isRequired,
  id: string.isRequired,
  isReadOnly: bool.isRequired,
  onBlur: func.isRequired // others that are required by <Input />

};

var Num = function Num(_ref5) {
  var id = _ref5.id,
      isReadOnly = _ref5.isReadOnly,
      isRequired = _ref5.isRequired,
      onBlur = _ref5.onBlur,
      onChange = _ref5.onChange,
      value = _ref5.value;

  var _useInputDebounce3 = useInputDebounce({
    onChange: onChange,
    value: value,
    wait: 1000
  }),
      val = _useInputDebounce3[0],
      setVal = _useInputDebounce3[1];

  return /*#__PURE__*/React.createElement("input", {
    className: "form-control",
    id: id,
    disabled: isReadOnly,
    onChange: setVal,
    onBlur: onBlur,
    required: isRequired,
    type: "number",
    value: val // `step` is required here or IE will throw error on decimal numbers
    // https://www.isotoma.com/blog/2012/03/02/html5-input-typenumber-and-decimalsfloats-in-chrome/
    ,
    step: "any"
  });
};

Num.propTypes = {
  id: string.isRequired,
  isReadOnly: bool.isRequired,
  isRequired: bool.isRequired,
  onBlur: func.isRequired,
  onChange: func.isRequired,
  value: string.isRequired
};

var DatePicker = function DatePicker(_ref6) {
  var end = _ref6.end,
      id = _ref6.id,
      isReadOnly = _ref6.isReadOnly,
      onBlur = _ref6.onBlur,
      onChange = _ref6.onChange,
      start = _ref6.start,
      value = _ref6.value;

  var handleDayChange = function handleDayChange(selectedDate) {
    if (selectedDate === void 0) {
      selectedDate = "";
    }

    var normalizer = function normalizer(date) {
      return date ? formatDate(date, DATE_FORMAT_PRODUCT) : "";
    };

    onChange(selectedDate, normalizer, {
      flush: true
    });
  }; // If there's a value, it's likely a product formatted value because it's
  // coming from xforms, so we'll convert to ISO because that's what
  // <InputDate /> expects


  var parsedDate = moment(value, DATE_FORMAT_PRODUCT);
  var parsedValue = parsedDate.isValid() ? parsedDate.format(DATE_FORMAT_ISO8601) : "";
  return /*#__PURE__*/React.createElement(InputDate, {
    id: id,
    isReadOnly: isReadOnly,
    value: parsedValue,
    onBlur: onBlur,
    onChange: handleDayChange // If there is a start and end date, those consititue an acceptable range
    // for the input's value. If they do exist, we'll use the API of
    // `react-day-picker` to set those dates as disabled.
    ,
    dayPickerProps: _extends({}, start && end ? {
      disabledDays: {
        before: moment(start, DATE_FORMAT_PRODUCT).toDate(),
        after: moment(end, DATE_FORMAT_PRODUCT).toDate()
      }
    } : {})
  });
}; // Expect a string with the format `MM/DD/YYYY`


var mmddyyyyString = function mmddyyyyString(props, propName, componentName) {
  if (props[propName] && !/\d{2}\/\d{2}\/\d{4}/.test(props[propName])) {
    return new Error("Invalid prop `" + propName + "` supplied to" + " `" + componentName + "`. Validation failed.");
  }
};

DatePicker.propTypes = {
  end: mmddyyyyString,
  id: string.isRequired,
  isReadOnly: bool.isRequired,
  onBlur: func.isRequired,
  onChange: func.isRequired,
  start: mmddyyyyString,
  value: string.isRequired
};

var Zipcode = function Zipcode(props) {
  return (
    /*#__PURE__*/
    // Only allow entering of digits
    React.createElement(Input, _extends({}, props, {
      value: props.value.replace(/[^\d,]+/g, "")
    }))
  );
};

Zipcode.propTypes = {
  value: string.isRequired
};

var SubmitButton = function SubmitButton(_ref7) {
  var label = _ref7.label,
      handleClick = _ref7.handleClick;

  var _useState = useState(false),
      isLoading = _useState[0],
      setLoading = _useState[1];

  var _useState2 = useState(""),
      error = _useState2[0],
      setError = _useState2[1];

  var onClick = /*#__PURE__*/function () {
    var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setError("");
              setLoading(true);
              _context.prev = 2;
              _context.next = 5;
              return handleClick();

            case 5:
              _context.next = 10;
              break;

            case 7:
              _context.prev = 7;
              _context.t0 = _context["catch"](2);
              setError(_context.t0.message);

            case 10:
              setLoading(false);

            case 11:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[2, 7]]);
    }));

    return function onClick() {
      return _ref8.apply(this, arguments);
    };
  }();

  return /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    disabled: isLoading,
    bsStyle: "primary",
    onClick: onClick
  }, label), error && /*#__PURE__*/React.createElement("p", {
    className: "text-danger",
    style: {
      paddingTop: "10px"
    }
  }, error));
};

SubmitButton.propTypes = {
  label: string.isRequired,
  handleClick: func.isRequired,
  passthroughProps: object.isRequired
};

var ActionButton = function ActionButton(_ref9) {
  var label = _ref9.label,
      handleClick = _ref9.handleClick;

  var _useState3 = useState(""),
      error = _useState3[0],
      setError = _useState3[1];

  var dispatch = useDispatch();

  var onClick = /*#__PURE__*/function () {
    var _ref10 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              setError("");
              dispatch({
                type: "IS_LOADING"
              });
              _context2.prev = 2;
              _context2.next = 5;
              return handleClick();

            case 5:
              _context2.next = 10;
              break;

            case 7:
              _context2.prev = 7;
              _context2.t0 = _context2["catch"](2);
              setError(_context2.t0.message);

            case 10:
              dispatch({
                type: 'LOAD_SUCCESS'
              });

            case 11:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, null, [[2, 7]]);
    }));

    return function onClick() {
      return _ref10.apply(this, arguments);
    };
  }();

  return /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    bsStyle: "primary",
    onClick: onClick
  }, label), error && /*#__PURE__*/React.createElement("p", {
    className: "text-danger",
    style: {
      paddingTop: "10px"
    }
  }, error));
};

ActionButton.propTypes = {
  label: string.isRequired,
  handleClick: func.isRequired
};
var customRenderers = {
  string: rexCustomRenderer(Input),
  number: rexCustomRenderer(Num),
  currency: rexCustomRenderer(Currency),
  enum: rexCustomRenderer(Select),
  range: rexCustomRenderer(DatePicker),
  date: rexCustomRenderer(DatePicker),
  zipcode: rexCustomRenderer(Zipcode),
  email: rexCustomRenderer(Input),
  button: rexCustomRenderer(ActionButton),
  submit: rexCustomRenderer(SubmitButton),
  fallback: rexCustomRenderer(Input)
};
var wrappers = {
  SubgroupWrapper: function SubgroupWrapper(_ref11) {
    var label = _ref11.label,
        children = _ref11.children,
        inputs = _ref11.inputs;
    return _SubgroupWrapper({
      label: label,
      children: children,
      inputs: inputs,
      colSpan: 3
    });
  },
  GroupWrapper: function GroupWrapper(_ref12) {
    var label = _ref12.label,
        children = _ref12.children,
        inputs = _ref12.inputs;
    return _GroupWrapper({
      label: label,
      children: children,
      inputs: inputs,
      colSpan: 3
    });
  },
  // We just want the direct children, but if we do <Fragment /> itself
  // react will throw a warning saying to not pass extra props to it
  // (which Rex does). So we'll swallow those extra props here
  // eslint-disable-next-line react/display-name, react/prop-types
  FormWrapper: function FormWrapper(_ref13) {
    var children = _ref13.children;
    return /*#__PURE__*/React.createElement(Fragment, null, children);
  },
  // eslint-disable-next-line react/display-name, react/prop-types
  RepeatGroupWrapper: function RepeatGroupWrapper(_ref14) {
    var children = _ref14.children;
    return /*#__PURE__*/React.createElement(Fragment, null, children);
  },
  // eslint-disable-next-line react/display-name, react/prop-types
  RepeatItemWrapper: function RepeatItemWrapper(_ref15) {
    var children = _ref15.children;
    return /*#__PURE__*/React.createElement(Fragment, null, children);
  }
};

var RexWrapper = /*#__PURE__*/function (_Component) {
  _inheritsLoose(RexWrapper, _Component);

  function RexWrapper() {
    return _Component.apply(this, arguments) || this;
  }

  var _proto = RexWrapper.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$props$pacData = this.props.pacData,
        apiPoliciesUrl = _this$props$pacData.apiPoliciesUrl,
        apiAddressServicingUrl = _this$props$pacData.apiAddressServicingUrl,
        PolicyID = _this$props$pacData.policyData.PolicyID;
    store.dispatch(updateConfig({
      services: {
        ixprofiler: {
          universal: apiPoliciesUrl + "/profiles/" + PolicyID
        },
        analysisvalidation: apiPoliciesUrl + "/iac/analysis/" + PolicyID,
        addressservicing: apiAddressServicingUrl
      }
    }));
  };

  _proto.render = function render() {
    var _this$props = this.props,
        derivedForm = _this$props.derivedForm,
        formSubmitAttempted = _this$props.formSubmitAttempted,
        overrideRulesetBlockers = _this$props.overrideRulesetBlockers,
        _this$props$pacData2 = _this$props.pacData,
        enableRulesets = _this$props$pacData2.enableRulesets,
        googleApiKey = _this$props$pacData2.googleApiKey,
        pacId = _this$props$pacData2.pacId,
        policyActionId = _this$props$pacData2.policyActionId,
        _this$props$pacData2$ = _this$props$pacData2.policyData,
        PolicyType = _this$props$pacData2$.PolicyType,
        StartDate = _this$props$pacData2$.StartDate,
        rulesets = _this$props.rulesets,
        columnData = _this$props.columnData,
        columnDataTitle = _this$props.columnDataTitle;
    var addressFusionDetails = getAddressFusionDetails(derivedForm);
    var columnTitle = columnData && !columnDataTitle ? " " : columnDataTitle;
    return /*#__PURE__*/React.createElement("table", {
      className: "table",
      style: {
        borderBottom: "1px solid #ddd"
      }
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      className: "col-xs-5"
    }), /*#__PURE__*/React.createElement("th", {
      className: "col-xs-3"
    }, "Value as of", " ", moment(StartDate, DATE_FORMAT_PRODUCT).format(DATE_FORMAT_UI)), columnData && columnTitle && /*#__PURE__*/React.createElement("th", {
      className: "col-xs-2"
    }, columnTitle), /*#__PURE__*/React.createElement("th", {
      className: "col-xs-4"
    }, "Proposed Changes"))), /*#__PURE__*/React.createElement(Rex, {
      derivedForm: derivedForm,
      enableRulesets: enableRulesets,
      passthroughProps: {
        formSubmitAttempted: formSubmitAttempted,
        overrideRulesetBlockers: overrideRulesetBlockers,
        rulesetsById: deriveRulesetsById(rulesets),
        googleApiKey: googleApiKey,
        isCommercial: PolicyType.toLowerCase() === "bop",
        addressFusionDetails: addressFusionDetails,
        columnData: columnData
      },
      renderers: customRenderers,
      wrappers: wrappers,
      useForm: {
        id: pacId,
        childId: policyActionId
      },
      dispatchers: {
        addressValidation: addressValidation,
        addressValidationLocation1: addressValidation,
        addressValidationLocation2: addressValidation,
        addressValidationLocation3: addressValidation,
        addressValidationLocation4: addressValidation,
        addressValidationLocation5: addressValidation,
        addressValidationLocation6: addressValidation,
        addressValidationLocation7: addressValidation,
        addressValidationLocation8: addressValidation,
        addressValidationLocation9: addressValidation,
        addressValidationLocation10: addressValidation,
        addressServicingValidation: addressServicingValidation,
        addressServicingValidationLocation1: addressServicingValidation,
        addressServicingValidationLocation2: addressServicingValidation,
        addressServicingValidationLocation3: addressServicingValidation,
        addressServicingValidationLocation4: addressServicingValidation,
        addressServicingValidationLocation5: addressServicingValidation,
        addressServicingValidationLocation6: addressServicingValidation,
        addressServicingValidationLocation7: addressServicingValidation,
        addressServicingValidationLocation8: addressServicingValidation,
        addressServicingValidationLocation9: addressServicingValidation,
        addressServicingValidationLocation10: addressServicingValidation,
        analysisValidation: analysisValidation
      },
      fusions: fusions
    }));
  };

  return RexWrapper;
}(Component);
/**
 * Transform the rulesets array from Rex to an object of rulesets grouped by the
 * form field id (represented by values in `highlightControls`). We do this so
 * individual custom renderers in Rex can easily test whether they have an
 * associated ruleset by checking for their field id in this object.
 *
 * In:
 * [
 *   {
 *     rulesetId: "1234"
 *     highlightControls: ["FormField1", "FormField2"]
 *     ...
 *   },
 *   {
 *     rulesetId: "5678"
 *     highlightControls: ["FormField2"]
 *     ...
 *   }
 * ]
 *
 * Out:
 * {
 *   FormField2: [
 *     {
 *       rulesetId: "1234"
 *       highlightControls: ["FormField1", "FormField2"]
 *        ...
 *     },
 *     {
 *       rulesetId: "5678"
 *       highlightControls: ["FormField2"]
 *       ...
 *     }
 *   ],
 *   FormField1: [
 *     {
 *       rulesetId: "1234"
 *       highlightControls: ["FormField1", "FormField2"]
 *       ...
 *     }
 *   ]
 * }
 *
 * @param {Object[]} rulesets
 * @return {Object}
 */


RexWrapper.propTypes = {
  derivedForm: array.isRequired,
  formSubmitAttempted: bool.isRequired,
  overrideRulesetBlockers: bool.isRequired,
  pacData: pacDataPropTypes.isRequired,
  rulesets: array.isRequired,
  columnData: object,
  columnDataTitle: string
};

function deriveRulesetsById(rulesets) {
  return rulesets.reduce(function (acc, ruleset) {
    if (ruleset.highlightControls) {
      ruleset.highlightControls.forEach(function (id) {
        if (acc[id]) {
          acc[id].push(ruleset);
        } else {
          acc[id] = [ruleset];
        }
      });
    }

    return acc;
  }, {});
}

export default RexWrapper;